import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, Plus } from 'lucide-react';
import { WEEKDAY_LABELS, type WorkScheduleDto } from '@workforce/shared';
import { formatMinutes, t } from '@/lib/i18n';
import { ApiError, api } from '@/lib/api';
import { useSchedules } from '@/lib/queries';
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Modal, Notice, PageHeader, Select, Spinner, Textarea, Toggle } from '@/components/ui';
import { WeekView } from './EmployeeDetail';

interface DayForm {
  enabled: boolean;
  startTime: string;
  endTime: string;
  breakMinutes: number;
}
type Form = {
  name: string;
  description: string;
  lateGraceMinutes: number;
  earlyLeaveGraceMinutes: number;
  attendanceCalcMode: '' | 'first_last' | 'sum_intervals';
  isActive: boolean;
  days: Record<number, DayForm>;
};

const blankDays = (enabled: number[]): Record<number, DayForm> =>
  Object.fromEntries([1, 2, 3, 4, 5, 6, 7].map((d) => [d, { enabled: enabled.includes(d), startTime: '09:00', endTime: '17:00', breakMinutes: 60 }]));

export function SchedulesPage() {
  const qc = useQueryClient();
  const schedules = useSchedules();
  const [editing, setEditing] = useState<WorkScheduleDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<WorkScheduleDto | null>(null);
  const [f, setF] = useState<Form | null>(null);

  const open = (s: WorkScheduleDto | 'new') => {
    setEditing(s);
    if (s === 'new') {
      setF({ name: '', description: '', lateGraceMinutes: 10, earlyLeaveGraceMinutes: 10, attendanceCalcMode: '', isActive: true, days: blankDays([1, 2, 3, 4, 5]) });
    } else {
      const days = blankDays([]);
      for (const d of s.days) days[d.weekday] = { enabled: true, startTime: d.startTime, endTime: d.endTime, breakMinutes: d.breakMinutes };
      setF({
        name: s.name,
        description: s.description ?? '',
        lateGraceMinutes: s.lateGraceMinutes,
        earlyLeaveGraceMinutes: s.earlyLeaveGraceMinutes,
        attendanceCalcMode: s.attendanceCalcMode ?? '',
        isActive: s.isActive,
        days,
      });
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f!.name,
        description: f!.description,
        lateGraceMinutes: Number(f!.lateGraceMinutes),
        earlyLeaveGraceMinutes: Number(f!.earlyLeaveGraceMinutes),
        attendanceCalcMode: f!.attendanceCalcMode || null,
        isActive: f!.isActive,
        days: Object.entries(f!.days)
          .filter(([, d]) => d.enabled)
          .map(([w, d]) => ({ weekday: Number(w), startTime: d.startTime, endTime: d.endTime, breakMinutes: Number(d.breakMinutes) })),
      };
      return editing === 'new' ? api.post('/schedules', body) : api.put(`/schedules/${(editing as WorkScheduleDto).id}`, body);
    },
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/schedules/${id}`),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
  const setDay = (w: number, patch: Partial<DayForm>) => setF((s) => (s ? { ...s, days: { ...s.days, [w]: { ...s.days[w]!, ...patch } } } : s));
  const fe = save.error instanceof ApiError ? save.error.fieldErrors() : {};

  return (
    <div>
      <PageHeader
        title={t('Work schedules')}
        subtitle={t("Working days and hours. Times are in each employee's own time zone.")}
        actions={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => open('new')}>
            {t('New schedule')}
          </Button>
        }
      />
      <ErrorBanner error={schedules.error} />
      {schedules.isLoading ? (
        <Spinner />
      ) : !schedules.data?.length ? (
        <Card>
          <EmptyState icon={<CalendarRange className="h-6 w-6" />} title={t('No schedules yet')} description={t('Create at least one schedule before adding employees.')} />
        </Card>
      ) : (
        <div className="space-y-4">
          {schedules.data.map((s) => (
            <Card
              key={s.id}
              title={
                <span className="flex flex-wrap items-center gap-2">
                  {s.name} {!s.isActive && <Badge>{t('Inactive')}</Badge>}
                  <span className="text-xs font-normal text-slate-500">
                    {t('{time} / week', { time: formatMinutes(s.weeklyMinutes) })} ·{' '}
                    {s.employeeCount === 1 ? t('{n} employee', { n: s.employeeCount }) : t('{n} employees', { n: s.employeeCount })} ·{' '}
                    {t('late grace {time}', { time: formatMinutes(s.lateGraceMinutes) })}
                  </span>
                </span>
              }
              actions={
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => open(s)}>
                    {t('Edit')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(s)}>
                    {t('Delete')}
                  </Button>
                </div>
              }
            >
              {s.description && <p className="mb-3 text-sm text-slate-500">{s.description}</p>}
              <WeekView days={s.days} />
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={!!editing && !!f}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? t('New work schedule') : t('Edit work schedule')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={save.isPending} disabled={!f?.name.trim()} onClick={() => save.mutate()}>
              {t('Save schedule')}
            </Button>
          </>
        }
      >
        {f && (
          <div className="space-y-5">
            <ErrorBanner error={save.error} />
            {editing !== 'new' && (editing as WorkScheduleDto).employeeCount > 0 && (
              <Notice tone="amber">
                {t(
                  'This schedule is used by {n} employee(s). Edits apply to them going forward. To change hours only from a certain date, create a new schedule and schedule a work-terms change on each employee instead.',
                  { n: (editing as WorkScheduleDto).employeeCount },
                )}
              </Notice>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t('Name')} required error={fe.name}>
                <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t('e.g. Sun–Thu 08:00–16:00')} />
              </Field>
              <Field label={t('Daily worked-time rule')} hint={t('Leave on company default unless this schedule needs its own rule.')}>
                <Select value={f.attendanceCalcMode} onChange={(e) => setF({ ...f, attendanceCalcMode: e.target.value as Form['attendanceCalcMode'] })}>
                  <option value="">{t('Company default')}</option>
                  <option value="first_last">{t('First sign-in → last sign-out')}</option>
                  <option value="sum_intervals">{t('Sum of all worked intervals')}</option>
                </Select>
              </Field>
              <Field label={t('Late arrival grace (minutes)')}>
                <Input type="number" min={0} max={240} value={f.lateGraceMinutes} onChange={(e) => setF({ ...f, lateGraceMinutes: Number(e.target.value) })} />
              </Field>
              <Field label={t('Early departure grace (minutes)')}>
                <Input type="number" min={0} max={240} value={f.earlyLeaveGraceMinutes} onChange={(e) => setF({ ...f, earlyLeaveGraceMinutes: Number(e.target.value) })} />
              </Field>
              <Field label={t('Description')} className="sm:col-span-2">
                <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
              </Field>
            </div>
            <div>
              <p className="label">{t('Working days')}</p>
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {[1, 2, 3, 4, 5, 6, 7].map((w) => {
                  const d = f.days[w]!;
                  return (
                    <div key={w} className="flex flex-wrap items-center gap-3 px-3 py-2">
                      <label className="flex w-32 items-center gap-2 text-sm font-medium">
                        <input type="checkbox" checked={d.enabled} onChange={(e) => setDay(w, { enabled: e.target.checked })} className="rounded border-slate-300" />
                        {t(WEEKDAY_LABELS[w as 1])}
                      </label>
                      {d.enabled ? (
                        <>
                          <Input type="time" value={d.startTime} onChange={(e) => setDay(w, { startTime: e.target.value })} className="w-28" aria-label={t('Start')} />
                          <span className="text-slate-400">–</span>
                          <Input type="time" value={d.endTime} onChange={(e) => setDay(w, { endTime: e.target.value })} className="w-28" aria-label={t('End')} />
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            {t('break')}
                            <Input type="number" min={0} max={600} value={d.breakMinutes} onChange={(e) => setDay(w, { breakMinutes: Number(e.target.value) })} className="w-20" aria-label={t('Break minutes')} />
                            {t('min')}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-slate-400">{t('Day off')}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-slate-500">{t('An end time earlier than the start time means an overnight shift.')}</p>
            </div>
            {editing !== 'new' && <Toggle checked={f.isActive} onChange={(v) => setF({ ...f, isActive: v })} label={t('Active')} />}
          </div>
        )}
      </Modal>
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={t('Delete {name}?', { name: deleting?.name })}
        message={t('Schedules that are or were used by any employee are kept for history and can only be deactivated.')}
        confirmLabel={t('Delete')}
        danger
        loading={del.isPending}
        error={del.error}
        onConfirm={() => deleting && del.mutate(deleting.id)}
      />
    </div>
  );
}
